'use client';

import { FilterChip } from './FilterChip';
import { type CategoryIndex, getCategoryMeta } from './categoryIndex';
import { DIRECTION_CHIP_LABEL, RECURRENCE_CHIP_LABEL } from './transactionType';
import type {
  PaymentMethodFilter,
  PaymentType,
  RecurrenceFilter,
  RefundFacet,
  TransactionDirection,
} from '@/lib/transactions/types';
import { type PlatformFilter, platformLabel } from '@/lib/transactions/platform';
import type { TransactionTag } from '@/types';
import type { AccountKind, AccountOption, DrawerKind } from './types';

interface Props {
  categoryIds: string[];
  tagIds: string[];
  /** The tag catalog, for resolving chip labels; ids without a match render as-is. */
  tags: readonly TransactionTag[];
  account: string | null;
  paymentMethod: PaymentMethodFilter;
  platform: PlatformFilter;
  direction: TransactionDirection | null;
  recurrence: RecurrenceFilter[];
  refund: RefundFacet;
  accounts: AccountOption[];
  categoryIndex: CategoryIndex;
  onOpen: (kind: Exclude<DrawerKind, null>) => void;
  onClearCategories: () => void;
  onClearTags: () => void;
  onClearAccount: () => void;
  onClearPaymentMethod: () => void;
  onClearPlatform: () => void;
  onClearDirection: () => void;
  onClearRecurrence: () => void;
  onClearRefund: () => void;
}

const PAYMENT_METHOD_LABELS: Record<PaymentType, string> = {
  pix: 'Pix',
  credit: 'Crédito',
  debit: 'Débito',
  boleto: 'Boleto',
  transfer: 'TED / DOC',
  other: 'Outros',
};

const ACCOUNT_KIND_LABEL: Record<AccountKind, string> = {
  credit: 'Crédito',
  checking: 'Conta',
  investment: 'Investimento',
};

const REFUND_FACET_LABELS: Record<Exclude<RefundFacet, null>, string> = {
  confirmed: 'Confirmados',
  pending: 'Aguardando',
};

function categoryChipLabel(ids: string[], idx: CategoryIndex): string {
  if (ids.length === 0) return 'Categoria';
  const first = getCategoryMeta(idx, ids[0]!).label;
  if (ids.length === 1) return `Cat · ${first}`;
  return `Cat · ${first} +${ids.length - 1}`;
}

function tagChipLabel(ids: string[], tags: readonly TransactionTag[]): string {
  if (ids.length === 0) return 'Tags';
  const first = tags.find((t) => t.id === ids[0])?.name ?? ids[0]!;
  if (ids.length === 1) return `Tags · ${first}`;
  return `Tags · ${first} +${ids.length - 1}`;
}

function recurrenceChipLabel(values: RecurrenceFilter[]): string {
  if (values.length === 0) return 'Recorrência';
  const first = RECURRENCE_CHIP_LABEL[values[0]!];
  if (values.length === 1) return `Recorrência · ${first}`;
  return `Recorrência · ${first} +${values.length - 1}`;
}

function accountChipShort(account: AccountOption): string {
  const parts = [account.bank, ACCOUNT_KIND_LABEL[account.type]];
  if (account.label) parts.push(account.label);
  const head = parts.join(' ');
  return account.last4 ? `${head} ····${account.last4}` : head;
}

export function FilterChipsRow({
  categoryIds,
  tagIds,
  tags,
  account,
  paymentMethod,
  platform,
  direction,
  recurrence,
  refund,
  accounts,
  categoryIndex,
  onOpen,
  onClearCategories,
  onClearTags,
  onClearAccount,
  onClearPaymentMethod,
  onClearPlatform,
  onClearDirection,
  onClearRecurrence,
  onClearRefund,
}: Props) {
  const selectedAccount = account ? accounts.find((a) => a.id === account) : undefined;
  const accountLabel = selectedAccount ? `Conta · ${accountChipShort(selectedAccount)}` : 'Conta';
  const paymentLabel = paymentMethod
    ? `Pagto · ${PAYMENT_METHOD_LABELS[paymentMethod]}`
    : 'Forma de pagamento';
  const platformChipLabel = platform
    ? `Canal · ${platformLabel(platform)}`
    : 'Canal de compra';
  const directionLabel = direction ? `Tipo · ${DIRECTION_CHIP_LABEL[direction]}` : 'Tipo';
  const recurrenceLabel = recurrenceChipLabel(recurrence);
  const refundLabel = refund ? `Reembolsos · ${REFUND_FACET_LABELS[refund]}` : 'Reembolsos';

  const openAccountDrawer = () => onOpen('account');
  const openCategoryDrawer = () => onOpen('category');
  const openPaymentMethodDrawer = () => onOpen('paymentMethod');
  const openPlatformDrawer = () => onOpen('platform');
  const openDirectionDrawer = () => onOpen('direction');
  const openRecurrenceDrawer = () => onOpen('recurrence');
  const openRefundDrawer = () => onOpen('refund');
  const openTagDrawer = () => onOpen('tag');

  return (
    <div className="scrollbar-hide -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 lg:flex-wrap lg:overflow-visible">
      <FilterChip
        active={!!direction}
        clearable
        label={directionLabel}
        onClick={openDirectionDrawer}
        onClear={onClearDirection}
      />
      <FilterChip
        active={!!account}
        clearable
        label={accountLabel}
        onClick={openAccountDrawer}
        onClear={onClearAccount}
      />
      <FilterChip
        active={categoryIds.length > 0}
        clearable
        label={categoryChipLabel(categoryIds, categoryIndex)}
        onClick={openCategoryDrawer}
        onClear={onClearCategories}
      />
      {/* After Categoria (VER-378): tags are the other user-authored facet. */}
      <FilterChip
        active={tagIds.length > 0}
        clearable
        label={tagChipLabel(tagIds, tags)}
        onClick={openTagDrawer}
        onClear={onClearTags}
      />
      <FilterChip
        active={!!paymentMethod}
        clearable
        label={paymentLabel}
        onClick={openPaymentMethodDrawer}
        onClear={onClearPaymentMethod}
      />
      {/* 5th in the row (Davi, 2026-08-15) — born off-screen on mobile, the row scrolls. */}
      <FilterChip
        active={!!platform}
        clearable
        label={platformChipLabel}
        onClick={openPlatformDrawer}
        onClear={onClearPlatform}
      />
      <FilterChip
        active={recurrence.length > 0}
        clearable
        label={recurrenceLabel}
        onClick={openRecurrenceDrawer}
        onClear={onClearRecurrence}
      />
      <FilterChip
        active={!!refund}
        clearable
        label={refundLabel}
        onClick={openRefundDrawer}
        onClear={onClearRefund}
      />
    </div>
  );
}
